// First-party analytics beacon. Sends pageviews + custom events to our own
// /api/collect (same-origin, so ad-blockers don't block it). No cookie, no
// device identifier; identity is derived server-side from a daily-rotating hash.
//
// Runs alongside GTM/GA4 (this is the first-party copy that also captures the
// ~20-40% of visitors who block third-party analytics). Best-effort: any failure
// is swallowed and never affects the page.
//
// AUTOMATIC MEASUREMENT (no per-page wiring needed), mirroring and extending
// GA4's enhanced measurement:
//   pageview         every route change
//   scroll           25/50/75/90% depth, once each, per page
//   cta_click        clicks on primary/secondary CTAs (see CTA_SELECTOR)
//   outbound_click   clicks to a different host
//   file_download    clicks to a document/archive/media file
//   user_engagement  active (tab-visible) seconds for the page being left
//
// EXPLICIT (needs a call site, because it needs semantic context):
//   form_start / form_submit / form_error   -> composables/useFormTracking.js
//   search                                  -> Element/Search/Bar.vue
//   article_scroll / article_read / ...     -> composables/useArticleEngagement.js
//
// Articles own their own scroll tracking (it carries the slug), so they call
// claimScroll() to suppress the generic scroll events and avoid double-counting.

// Extensions that count as a download rather than a navigation.
const DOWNLOAD_RE =
  /\.(pdf|docx?|xlsx?|pptx?|csv|zip|rar|7z|gz|tar|mp3|mp4|mov|wav|avi|dmg|pkg|exe)(\?|#|$)/i

// What counts as a CTA: an EXPLICIT opt-in, nothing else.
//
// This was previously inferred from the `rounded-md` utility class, which is a
// styling choice and not a statement of intent. That swept in the psychosocial
// quiz's Previous/Next wizard buttons (~10 rows per run), FAQ disclosure
// toggles and the locale switcher, while missing real CTAs styled with
// rounded-lg or rounded-full. A utility class cannot express "this is a
// conversion action", so the marker is now set deliberately at the call site.
//
// Element/Button.vue sets it for its real button types; anything else opts in
// by putting `data-cta` (optionally with `data-cta-label`) on the element.
const CTA_SELECTOR = '[data-cta]'

export default defineNuxtPlugin((nuxtApp) => {
  if (!process.client) return

  const noop = () => {}

  // Analytics must never break a page. send(), measureScroll() and onDocClick()
  // guard themselves; this wraps the remaining entry points that are reached
  // from a router hook, a timer or an event listener, so nothing here can
  // surface as an uncaught error on a real navigation.
  const safe = (fn) => (...args) => {
    try {
      return fn(...args)
    } catch {
      /* never throw from analytics */
    }
  }

  // Only collect on the real production host. Preview deploys (*.vercel.app) and
  // localhost run the same build; without this they'd write dev/preview traffic
  // into the production analytics DB. Mirrors the gtm.client.js gate.
  if (!isProductionHost(window.location.hostname)) {
    nuxtApp.provide('track', noop)
    nuxtApp.provide('analyticsClaimScroll', noop)
    return
  }

  // Respect Do-Not-Track / Global Privacy Control: send nothing.
  const dnt =
    navigator.doNotTrack === '1' ||
    window.doNotTrack === '1' ||
    navigator.globalPrivacyControl === true
  if (dnt) {
    // Still expose no-ops so callers don't error.
    nuxtApp.provide('track', noop)
    nuxtApp.provide('analyticsClaimScroll', noop)
    return
  }

  const userStore = useUserStore()
  let firstView = true

  // Captured at plugin setup, which runs BEFORE the app mounts. The store's UTM
  // values are written by trk() in layouts/default.vue from onMounted, so the
  // very first pageview would otherwise carry nulls. That gap used to show up
  // honestly as '(none)/(none)'; with the referrer fallback in the rollup it
  // would instead be recorded as '<referrer>/referral', reporting a paid
  // landing as organic referral and splitting one session across two sources.
  const landingParams = (() => {
    try {
      const q = new URL(window.location.href).searchParams
      // Normalised the same way layouts/default.vue normalises before writing
      // the store, and gclid is NOT widened to wbraid/gbraid here. Without
      // both, the landing pageview and every later pageview of the same visit
      // land in different source_medium buckets and carry different click ids.
      const src = q.get('utm_source')
      const med = q.get('utm_medium')
      return {
        utmSource: src === 'linkedin.com' ? 'linkedin' : src,
        utmMedium: med === 'cpc' ? 'paid' : med,
        utmCampaign: q.get('utm_campaign'),
        gclid: q.get('gclid'),
      }
    } catch {
      return {}
    }
  })()

  // `pathOverride` exists for events that describe a page the browser has
  // already navigated away from. window.location is the DESTINATION by the time
  // a router hook runs, so user_engagement would otherwise be attributed to the
  // next page instead of the one the time was spent on.
  const send = (eventName, extra = {}, pathOverride = null) => {
    try {
      const payload = {
        event: eventName,
        path: pathOverride || window.location.pathname,
        referrer: firstView ? document.referrer || null : null,
        utmSource: userStore.utmSource || landingParams.utmSource || null,
        utmMedium: userStore.utmMedium || landingParams.utmMedium || null,
        utmCampaign: userStore.utmCampaign || landingParams.utmCampaign || null,
        gclid: userStore.gclid || landingParams.gclid || null,
        props: {
          ...extra,
          // screen size only on pageview (cheap context)
          ...(eventName === 'pageview'
            ? { sw: window.screen?.width, sh: window.screen?.height }
            : {}),
        },
      }
      const body = JSON.stringify(payload)
      // sendBeacon survives page unload and is non-blocking; fall back to fetch.
      if (navigator.sendBeacon) {
        navigator.sendBeacon('/api/collect', new Blob([body], { type: 'application/json' }))
      } else {
        fetch('/api/collect', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
        }).catch(() => {})
      }
    } catch {
      /* never throw from analytics */
    }
  }

  // NO client-side storage, deliberately. The collector's sign-off basis is
  // "no cookie, no device identifier -> no consent banner"
  // (scripts/analytics/README.md), and ePrivacy Art 5(3) covers storage in
  // terminal equipment whether or not it is a cookie. A session clock would
  // have to be persisted somewhere, so sessions are NOT measured here; GA4
  // already reports them. `visitors` (distinct daily hash, computed
  // server-side) is the per-person metric this collector provides.

  // --- engagement time ------------------------------------------------------
  // Active seconds on the current page, counting only while the tab is visible.
  // This is the metric behind GA4's "average engagement time" and the main thing
  // a pageview count cannot tell you.
  //
  // Reported as DELTAS, because a page can be backgrounded and returned to many
  // times: a single latched send would drop everything after the first
  // background, which is exactly the long mobile session worth measuring. Only
  // the first delta for a page is counted as an engaged pageview (the rest carry
  // continuation:true) so the rollup's average has a correct denominator.
  // "Engaged" means visible AND focused AND not idle, which is what GA4
  // measures. visibilityState alone stays 'visible' for a window sitting behind
  // another application, so a tab parked overnight would accrue hours and
  // dominate the average.
  const IDLE_AFTER_MS = 60_000 // no interaction for this long = not engaged
  const HEARTBEAT_MS = 5_000

  let activeMs = 0
  let reportedMs = 0
  let lastTick = Date.now()
  let lastInteraction = Date.now()

  // Tracked explicitly rather than read from the document at accrue time.
  // `visibilitychange` fires AFTER document.visibilityState has already flipped,
  // so testing it there attributes the elapsed interval to the state the page is
  // entering instead of the one it was in: going hidden would discard the time
  // just spent reading, and returning would count the entire background period
  // as active. This flag holds the state that interval was actually spent in.
  const engagedNow = () =>
    document.visibilityState === 'visible' &&
    document.hasFocus() &&
    Date.now() - lastInteraction < IDLE_AFTER_MS
  let wasEngaged = engagedNow()

  const accrue = safe(() => {
    const now = Date.now()
    // Cap a single interval at one heartbeat plus slack. Without this, a tab
    // that was engaged, then blurred without firing an event we listen for,
    // would have the entire gap credited on the next accrue.
    if (wasEngaged) activeMs += Math.min(now - lastTick, HEARTBEAT_MS * 2)
    lastTick = now
    wasEngaged = engagedNow()
  })

  // Sampling the state on a timer is what makes the idle cap and the blur case
  // work: neither produces a DOM event of its own.
  const heartbeat = setInterval(accrue, HEARTBEAT_MS)
  // pointermove is throttled: unthrottled it wrote on every mouse move, 60-120
  // times a second, for the lifetime of the app.
  for (const ev of ['pointerdown', 'keydown', 'scroll']) {
    window.addEventListener(ev, () => { lastInteraction = Date.now() }, { passive: true })
  }
  window.addEventListener('pointermove', () => {
    const now = Date.now()
    if (now - lastInteraction > 1000) lastInteraction = now
  }, { passive: true })
  // Refresh the idle clock on focus. accrue() alone recomputed engagement
  // against a stale lastInteraction, so a reader returning after more than
  // IDLE_AFTER_MS stayed "idle" and accrued nothing until a pointer or key
  // event fired, which on a short page can be the whole visit.
  window.addEventListener('focus', () => {
    lastInteraction = Date.now()
    accrue()
  })
  window.addEventListener('blur', accrue)

  const flushEngagement = safe((pathOverride = null) => {
    accrue()
    // Guard the RAW milliseconds before rounding. Rounding first let any slice
    // over 500ms through as a full second, so every fast click-through entered
    // engaged_pageviews (the divisor for average engagement time) at an
    // overstated duration, which is the opposite of what this guard is for.
    const deltaMs = activeMs - reportedMs
    if (deltaMs < 1000) return
    const deltaSeconds = Math.floor(deltaMs / 1000)
    const isContinuation = reportedMs > 0
    reportedMs = activeMs
    send(
      'user_engagement',
      { seconds: deltaSeconds, ...(isContinuation ? { continuation: true } : {}) },
      pathOverride
    )
  })

  const resetEngagement = () => {
    activeMs = 0
    reportedMs = 0
    lastTick = Date.now()
    lastInteraction = Date.now()
    wasEngaged = engagedNow()
  }

  document.addEventListener('visibilitychange', () => {
    accrue()
    // Leaving the tab is the last reliable moment on mobile, where pagehide
    // often never fires. Returning resumes accrual; the next flush sends only
    // the additional time.
    if (document.visibilityState === 'hidden') flushEngagement(currentPath())
  })
  window.addEventListener('pagehide', () => flushEngagement(currentPath()))

  // --- scroll depth ---------------------------------------------------------
  // Fires once per threshold per page. Articles suppress this via claimScroll()
  // because useArticleEngagement already reports depth with the article slug.
  //
  // The claim is keyed to a PATH rather than a boolean: useArticleEngagement
  // runs in onMounted, which Vue flushes in a microtask, while the pageview is
  // sent from a setTimeout macrotask. A boolean set in onMounted would be reset
  // by the pageview that follows it, so the suppression would silently only work
  // on a hard load and not on any in-site navigation.
  const SCROLL_MARKS = [25, 50, 75, 90]
  let scrollFired = new Set()
  let scrollClaimedFor = null
  let scrollRaf = null
  let trackedPath = null

  function currentPath() {
    return trackedPath || window.location.pathname
  }

  const measureScroll = () => {
    scrollRaf = null
    try {
      if (scrollClaimedFor && scrollClaimedFor === currentPath()) return
      if (scrollFired.size === SCROLL_MARKS.length) return // nothing left to report
      const doc = document.documentElement
      const scrollable = doc.scrollHeight - window.innerHeight
      // A page shorter than the viewport has nothing to scroll; reporting 90%
      // for it would inflate the depth metric with pages nobody scrolled.
      if (scrollable <= 40) return
      const pct = Math.min(100, Math.round((window.scrollY / scrollable) * 100))
      for (const m of SCROLL_MARKS) {
        if (pct >= m && !scrollFired.has(m)) {
          scrollFired.add(m)
          send('scroll', { percent: m })
        }
      }
    } catch {
      /* never throw from analytics */
    }
  }

  // Coalesce to one measurement per frame: the raw handler reads scrollHeight
  // and innerHeight, which force layout, and on articles it runs alongside
  // useArticleEngagement's own handler.
  const onScroll = safe(() => {
    if (scrollRaf !== null) return
    scrollRaf = requestAnimationFrame(measureScroll)
  })
  window.addEventListener('scroll', onScroll, { passive: true })

  // --- delegated click tracking --------------------------------------------
  // One listener on the document catches CTAs, outbound links and downloads
  // everywhere on the site, so no component needs to opt in.
  const labelFor = (el) => {
    const explicit = el.getAttribute?.('data-cta-label') || el.getAttribute?.('aria-label')
    const text = explicit || el.textContent || ''
    return (
      text
        .replace(/\s+/g, ' ')
        // Decorative trailing glyphs (the arrow in Element/Button.vue) are part
        // of textContent, so the same CTA would otherwise appear twice in the
        // permanent dimension: once with the arrow and once without.
        .replace(/[\u2190-\u21FF\u2B00-\u2BFF>\s]+$/, '')
        .trim()
        .slice(0, 80) || null
    )
  }

  // One click produces exactly ONE event. Destination classification (download /
  // outbound / internal) and CTA identity are orthogonal, so the CTA label rides
  // along on whichever event fires rather than emitting a second one. Emitting
  // both would double-count the action in the generic `event` metric.
  const onDocClick = (e) => {
    try {
      const el = e.target?.closest?.('a[href], button')
      if (!el) return

      // An explicit data-cta wins over both heuristics below. `el.type` defaults
      // to 'submit' for a bare <button>, so without this the submit guard
      // silently killed every deliberately-marked button CTA.
      const explicitCta = el.hasAttribute?.('data-cta')

      if (el.tagName === 'BUTTON' && !explicitCta) {
        // A form's submit button is already reported as form_submit, with the
        // form name and outcome attached. Counting it here too would
        // double-count the action and fill the CTA dimension with "Subscribe".
        // `el.type`, not getAttribute: the IDL property defaults to 'submit'
        // whether or not the attribute is present or the button is in a form.
        // pricing.vue's quote button has neither, so the attribute check let
        // the site's highest-intent submission through as a CTA click.
        if (el.type === 'submit') return
        // Modal dismiss controls carry the same utility classes as real CTAs
        // and an aria-label of "Close ...", which labelFor would happily record.
        if (/^close\b/i.test(el.getAttribute('aria-label') || '')) return
      }

      // Resolve CTA identity FIRST, so it survives whatever the destination
      // turns out to be. Previously an off-domain CTA returned at the outbound
      // branch and lost both its label and its CTA identity.
      // data-no-cta opts a subtree out: the locale switcher's region links carry
      // CTA utility classes but are navigation, not conversion actions.
      const isCta = !el.closest?.('[data-no-cta]') && !!el.matches?.(CTA_SELECTOR)
      const label = isCta ? labelFor(el) : null
      const ctaProps = isCta ? { cta: true, label } : {}

      const href = el.getAttribute?.('href') || ''
      const isLink = el.tagName === 'A' && href

      if (!isLink) {
        // A button: only interesting if it reads as a CTA.
        if (isCta) send('cta_click', { ...ctaProps, href: null, kind: 'button' })
        return
      }

      if (/^(mailto|tel):/i.test(href)) {
        // Gated on isCta like every other branch. Ungated, every plain
        // "hello@elker.com" mailto in body copy ranked as a CTA.
        if (isCta) send('cta_click', { ...ctaProps, href: href.slice(0, 120), kind: 'contact' })
        return
      }

      // In-page anchor. Still a CTA click when it looks like one: the campaign
      // landing pages' primary button is an href="#kit" that scrolls to the
      // gated form, and returning early here dropped it entirely.
      if (href.startsWith('#')) {
        if (isCta) send('cta_click', { ...ctaProps, href: href.slice(0, 120), kind: 'anchor' })
        return
      }

      let url = null
      try {
        url = new URL(href, window.location.origin)
      } catch {
        url = null
      }
      if (!url) {
        if (isCta) send('cta_click', { ...ctaProps, href: href.slice(0, 120), kind: 'link' })
        return
      }

      const isExternal = !!url.host && url.host !== window.location.host

      // Host is tested as part of the download classification, not after it.
      // This site links a lot of legislation.gov.au / Safe Work / AHRC PDFs, and
      // testing the extension first filed every one of them as a first-party
      // download with the host thrown away.
      if (DOWNLOAD_RE.test(url.pathname)) {
        send('file_download', {
          ...ctaProps,
          href: (isExternal ? url.href : url.pathname).slice(0, 200),
          host: isExternal ? url.host : null,
          external: isExternal,
        })
        return
      }

      if (isExternal) {
        send('outbound_click', { ...ctaProps, host: url.host, href: url.href.slice(0, 200) })
        return
      }

      if (isCta) send('cta_click', { ...ctaProps, href: href.slice(0, 120), kind: 'link' })
    } catch {
      /* never throw from analytics */
    }
  }
  document.addEventListener('click', onDocClick, { capture: true, passive: true })

  // --- pageview -------------------------------------------------------------
  // Pageview on every route change + the initial load, de-duplicated by path so
  // the initial view isn't double-counted if router.afterEach also fires for the
  // first resolved route (depends on Nuxt/Vue Router hydration timing).
  const sendPageview = safe(() => {
    const path = window.location.pathname
    if (path === trackedPath) return // already recorded this path (dedup initial)

    // Close out the page we are leaving, attributed to THAT path, before the
    // new one starts.
    if (trackedPath !== null) flushEngagement(trackedPath)

    trackedPath = path

    send('pageview')
    firstView = false

    // Reset per-page state for the new route. scrollClaimedFor is deliberately
    // NOT cleared: it is keyed to a path, so a stale claim cannot match the new
    // one, and clearing it here would race with onMounted (see above).
    resetEngagement()
    scrollFired = new Set()
    // Actually cancel the queued frame. Clearing a flag left the callback
    // scheduled, so it still ran after trackedPath had moved and measured the
    // old scroll offset against the new page.
    if (scrollRaf !== null) {
      cancelAnimationFrame(scrollRaf)
      scrollRaf = null
    }

    // Deliberately NOT measuring scroll on arrival. It cannot report anything
    // useful: measureScroll already returns early for pages shorter than the
    // viewport, and a taller page legitimately has 0% depth until the visitor
    // scrolls. What it COULD do is fire 25/50/75/90 immediately, because on an
    // in-site navigation the router's scroll restoration has not necessarily
    // reset window.scrollY by the time this macrotask runs, so a reader
    // arriving from the bottom of a long article would have the old offset
    // measured against the new page.
  })

  const router = useRouter()
  router.afterEach(() => {
    setTimeout(sendPageview, 0) // let window.location reflect the new route
  })
  // initial load
  sendPageview()

  // Expose useTrack() for custom events (article engagement, form events, etc.).
  nuxtApp.provide('track', (eventName, props = {}) => send(eventName, props))

  // Let a page take ownership of scroll reporting (articles do: they report
  // depth with the slug attached, so the generic events would double-count).
  nuxtApp.provide('analyticsClaimScroll', () => {
    scrollClaimedFor = window.location.pathname
  })
})
